import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Checkbox,
  NotificationCard,
  SectionEyebrow,
  useToast,
} from '@sighthost/ui';
import { getPartnerToken } from '@sighthost/config';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * Phase10OrderPage — single-form funnel for Phase 10 once-off / first-month-upfront SKUs.
 *
 * Used by:
 *   /app/order/services/care-plan      → sku_code='care-pro'    (R199 first month)
 *   /app/order/services/email-setup    → sku_code='email-setup' (R350 once-off)
 *
 * Studio Starter has its own funnel (Slice 10.2) because it needs the deposit
 * slider + plan_months selector.
 *
 * Spec: _bmad-output/PHASE_10_SERVICES_BRD.md §5.5
 *       _bmad-output/PHASE_10_SERVICES_ARCHITECTURE.md §2.1
 *
 * Flow:
 *   1. Confirm details + accept terms (auth gate at submit)
 *   2. POST service-order-create → get { invoice_id, payment_token }
 *   3. Navigate to /pay/:invoice_id?token=… (Phase 9 public-pay route)
 */

interface Phase10OrderPageProps {
  skuCode: 'care-pro' | 'email-setup' | 'site-boost' | 'gbp-setup';
}

const SKU_COPY = {
  'care-pro': {
    title: 'Subscribe to SightHost Care',
    subtitle: 'Monthly support — first month payable upfront.',
    priceLabel: 'R199 / month',
    upfrontLabel: 'First month upfront — R199',
    description: 'Up to 10 mailboxes plus 60 minutes of website / email support every month. Subscription renews automatically; cancel anytime from your portal.',
    termsAnchor: '/terms#care-plan',
    learnMore: '/services/care',
    returnPath: '/app/order/services/care-plan',
    note: null,
  },
  'email-setup': {
    title: 'Book Email Setup + cPanel Training',
    subtitle: 'One-off setup for up to 5 mailboxes plus a 60-minute live training session.',
    priceLabel: 'R350 once-off',
    upfrontLabel: 'Total — R350',
    description: 'A team member will call you within 1 business day to schedule the setup + training session. Up to 5 mailboxes, 60 minutes of cPanel training included.',
    termsAnchor: '/terms#email-setup-service',
    learnMore: '/services/email-setup',
    returnPath: '/app/order/services/email-setup',
    note: null,
  },
  // SV-3 (2026-07-17) — Site Boost + Local Visibility one-offs
  'site-boost': {
    title: 'Order SightHost Site Boost',
    subtitle: 'Applied speed + on-page SEO optimisation with a before/after report.',
    priceLabel: 'R499 once-off',
    upfrontLabel: 'Total — R499',
    description: 'We optimise your website’s loading speed and on-page SEO fundamentals (titles, descriptions, headings, image compression, caching) and send you a before/after report. Delivered within 48 working hours of receiving access. No ranking promises — we improve your scores and fundamentals.',
    termsAnchor: '/terms#site-boost',
    learnMore: '/services/care',
    returnPath: '/app/order/services/site-boost',
    note: 'Works on SightHost-hosted sites and most external sites. For sites hosted elsewhere or built on closed platforms (e.g. Wix), some deliverables (server caching) may not apply — we’ll confirm applicability before starting.',
  },
  'gbp-setup': {
    title: 'Order Local Visibility Setup (Google Business Profile)',
    subtitle: 'Claim, verify and fully optimise your Google Business Profile.',
    priceLabel: 'R499 once-off',
    upfrontLabel: 'Total — R499',
    description: 'We claim (or create) your Google Business Profile, support you through Google’s verification, optimise categories, services, photos and posts, and hand over a review QR kit so customers can rate you in one scan.',
    termsAnchor: '/terms#gbp-setup',
    learnMore: '/services/care',
    returnPath: '/app/order/services/gbp-setup',
    note: 'Google controls verification timing (postcard / video / phone) — delays on Google’s side are outside our control, but we stay on it with you until the profile is live.',
  },
} as const;

export default function Phase10OrderPage({ skuCode }: Phase10OrderPageProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const copy = SKU_COPY[skuCode];

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [quoteIssued, setQuoteIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submitOrder(asQuotation: boolean) {
    setError(null);

    if (!termsAccepted) {
      setError('Please accept the terms and conditions to continue.');
      return;
    }

    if (!user) {
      navigate(`/sign-up?return=${encodeURIComponent(copy.returnPath)}`);
      return;
    }

    if (asQuotation) setQuoting(true); else setSubmitting(true);
    try {
      const idempotencyKey = crypto.randomUUID();

      const { data, error: efErr } = await supabase.functions.invoke<{
        ok: true;
        service_order_id: string;
        invoice_id: string;
        invoice_number: string;
        payment_token: string;
        total_cents: number;
        doc_type?: 'quotation' | 'invoice';
      }>('service-order-create', {
        body: {
          sku_code: skuCode,
          terms_accepted: true,
          as_quotation: asQuotation,
          // Partner attribution (2026-09-25). Captured at the portal root from
          // `?pt=`; resolved server-side; written only if the profile is not
          // attributed yet. Absent → house attribution, silently.
          ...(getPartnerToken() ? { partner_token: getPartnerToken() } : {}),
        },
        headers: { 'idempotency-key': idempotencyKey },
      });

      if (efErr) {
        let msg = efErr.message;
        const ctx = (efErr as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = await ctx.clone().json();
            if (parsed?.code === 'email_not_verified') {
              msg = 'Please verify your email address before placing an order. Check your inbox for the verification link.';
            } else if (parsed?.message) {
              msg = parsed.message;
            }
          } catch { /* fall back */ }
        }
        setError(msg);
        return;
      }

      if (!data?.ok) {
        setError('Order could not be created. Please try again or contact support.');
        return;
      }

      if (asQuotation) {
        // SV-3: quotation path — no checkout redirect. The QUO- document with
        // its pay-to-accept link is emailed; paying it converts it in place.
        setQuoteIssued(data.invoice_number);
        toast.toast({
          tone: 'success',
          title: 'Quotation on its way',
          body: `Quotation ${data.invoice_number} has been emailed to you.`,
        });
        return;
      }

      // Hand off to Phase 9 public-pay route. Navigates browser to PayFast checkout.
      const payUrl = `/pay/${data.invoice_id}?token=${data.payment_token}`;
      toast.toast({
        tone: 'success',
        title: 'Order ready',
        body: `Invoice ${data.invoice_number} created. Redirecting to PayFast…`,
      });
      window.location.href = payUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
      setQuoting(false);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submitOrder(false);
  }

  return (
    <UniversalPage
      title={copy.title}
      breadcrumbs={[
        { label: 'Services', href: '/app/services' },
        { label: copy.title },
      ]}
    >
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <div className="p-6 space-y-4">
            <div>
              <SectionEyebrow className="mb-2">{copy.priceLabel}</SectionEyebrow>
              <h2 className="font-display text-2xl font-bold text-foreground mt-1">{copy.title}</h2>
              <p className="text-muted-foreground mt-2">{copy.subtitle}</p>
            </div>

            <div className="bg-muted rounded-soft p-4 border border-border">
              <p className="text-muted-foreground text-sm">{copy.description}</p>
              {copy.note && (
                <p className="text-muted-foreground text-xs mt-3 border-t border-border pt-3">
                  {copy.note}
                </p>
              )}
            </div>

            {user ? (
              <div className="bg-muted rounded-soft p-4 border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Billing email</p>
                <p className="text-foreground font-medium">{user.email}</p>
                <p className="text-muted-foreground text-xs mt-2">
                  Need to use a different email? <Link to="/app/settings/profile" className="text-primary-ink hover:underline">Update your profile</Link> first.
                </p>
              </div>
            ) : (
              <NotificationCard
                tone="info"
                title="Sign in to continue"
                body="You'll be prompted to sign in (or create an account) when you submit. Your details will be remembered."
              />
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={termsAccepted}
                  onCheckedChange={(v) => setTermsAccepted(v === true)}
                  aria-label="Accept terms and conditions"
                />
                <span className="text-sm text-muted-foreground">
                  I've read and accept the{' '}
                  <a
                    href={copy.termsAnchor}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-ink hover:underline"
                  >
                    {skuCode === 'care-pro'
                      ? 'Care Plan terms'
                      : skuCode === 'email-setup'
                        ? 'Email Setup terms'
                        : skuCode === 'site-boost'
                          ? 'Site Boost terms'
                          : 'Local Visibility terms'}
                  </a>
                  , including the non-refundable clause once work has commenced.
                </span>
              </label>

              {error && (
                <NotificationCard tone="danger" title="Could not place order" body={error} />
              )}

              {quoteIssued && (
                <NotificationCard
                  tone="success"
                  title={`Quotation ${quoteIssued} emailed`}
                  body="Check your inbox — the quotation is valid for 14 days and carries a one-click pay link. Paying it accepts the quotation and confirms your order."
                />
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <Button asChild variant="ghost">
                  <Link to={copy.learnMore}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Link>
                </Button>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    loading={quoting}
                    disabled={submitting || quoting || !termsAccepted || !!quoteIssued}
                    onClick={() => void submitOrder(true)}
                  >
                    {quoting ? 'Preparing quotation…' : 'Email me a quotation'}
                  </Button>
                  <Button
                    type="submit"
                    size="lg"
                    variant="marketing"
                    loading={submitting}
                    disabled={submitting || quoting || !termsAccepted}
                  >
                    {submitting ? 'Creating order…' : copy.upfrontLabel}
                    {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Payment processed securely by PayFast. Receipt emailed to your billing address.
        </p>
      </div>
    </UniversalPage>
  );
}
